import {
  SnapRoundingDecimalPlaces,
  SnapRoundingMethod,
  type SnapRoundingOptions,
} from './types';

export * from './types';

// Multiplies by 10 ** n by adjusting the exponent of the value's own
// exponential representation, rather than by string-concatenating an
// `e${n}` suffix onto `String(value)`. String(value) switches to
// exponential notation itself once |value| < 1e-6 or >= 1e21 (e.g.
// String(1e-7) === '1e-7'), which would otherwise produce an invalid
// double-exponent literal like '1e-7e8' and silently parse to NaN.
function shift(value: number, exponentDelta: number): number {
  const [mantissa, exponent = '0'] = value.toExponential().split('e');

  return Number(`${mantissa}e${Number(exponent) + exponentDelta}`);
}

// Mirrors decimal.js's ROUND_HALF_UP: rounds to the nearest neighbour,
// and on a tie rounds away from zero. Shifting via exponential notation
// (rather than multiplying by 10 ** decimalPlaces) avoids the binary
// floating-point error that would otherwise misround ties like 1.005.
function toDecimalPlaces(value: number, decimalPlaces: number): number {
  if (!Number.isFinite(value)) {
    return value;
  }

  const sign = value < 0 ? -1 : 1;
  const absValue = Math.abs(value);

  const shifted = shift(absValue, decimalPlaces);
  const rounded = Math.round(shifted);
  const unshifted = shift(rounded, -decimalPlaces);

  return sign * unshifted;
}

export function snapRound(value: number, options: SnapRoundingOptions): number {
  const { method } = options;

  switch (method) {
    case SnapRoundingMethod.None:
      return value;

    case SnapRoundingMethod.Percent:
      return toDecimalPlaces(value, SnapRoundingDecimalPlaces.Percent);

    case SnapRoundingMethod.DecimalPercent:
      return toDecimalPlaces(value, SnapRoundingDecimalPlaces.DecimalPercent);

    case SnapRoundingMethod.Decimal:
      return toDecimalPlaces(value, SnapRoundingDecimalPlaces.Decimal);

    case SnapRoundingMethod.Custom: {
      const { decimalPlaces } = options;

      return toDecimalPlaces(value, decimalPlaces);
    }

    /* v8 ignore next 4 lines */
    default: {
      const exhaustive: never = method;
      throw new Error(`Unhandled rounding method: ${exhaustive}`);
    }
  }
}

export function snapRoundPercent(value: number) {
  return snapRound(value, { method: SnapRoundingMethod.Percent });
}

export function snapRoundDecimalPercent(value: number) {
  return snapRound(value, { method: SnapRoundingMethod.DecimalPercent });
}

export function snapRoundDecimal(value: number) {
  return snapRound(value, { method: SnapRoundingMethod.Decimal });
}

/**
 * Rounds to a precision with no unit interpretation attached — the caller is
 * asserting that the value and `decimalPlaces` already share a basis.
 *
 * Mirrors the Decimal implementation: anything rounding a *stored* per-unit
 * value needs a {@link SnapPricingBasis} rather than a bare precision.
 */
export function snapRoundCustom(value: number, decimalPlaces: number) {
  return toDecimalPlaces(value, decimalPlaces);
}
